<template>
  <main class="home">
    <Header
      v-if="showHeader"
      :navbarType="navbarType"
      :show-help-btn="navbarType === 'initial'"
      :screenTitle="screenTitle"
      @clicked="goBack()"
      :show-back-btn="showBackBtn"
    />
    <div v-if="noOutstandingBalance">
      <NoOutstandingBalance />
    </div>
    <div v-else>
      <div class="main-body" style="padding-top: 0">
        <Alert v-if="showAlert" :message="alertMessage" @onClose="closeAlert" />
        <div class="container" style="padding-top: 0">
          <MxPaymentMethods v-if="displayMxMethods" />
          <CoPaymentMethods v-if="displayCoMethods" />
          <AmountPickerV1 v-if="currentStep === 'chooseAmount-a'" />
          <AmountPickerV2 v-if="currentStep === 'chooseAmount-b'" />
          <CustomAmountPicker v-if="currentStep === 'keypad'" />
          <InstructionsContainer v-if="currentStep === 'instructions'" />
        </div>
      </div>
    </div>
  </main>
</template>

<script setup lang="ts">
import {
  computed,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  ref,
  getCurrentInstance,
  provide,
} from "vue";
import Header from "@/shared-components/consumer/Header.vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import { Service } from "@/apps/credit-line/services/network.service";
import { AndroidInterface } from "@/apps/credit-line/services/android.service";
import { PaymentMethod } from "@/apps/credit-line/types/payment";
import { IPaymentData } from "@/apps/credit-line/models/IPaymentData";
import currencyFormat from "@/apps/credit-line/helpers/currencyFormatter.helper";
import Alert from "@/shared-components/common/Alert.vue";
import { ErrorHandler } from "@/apps/credit-line/helpers/errorHandlerWithLogger.helper";
import { getErrorMessage } from "@/apps/credit-line/helpers/backCodesStrings.helper";
import { AuthenticationHelper } from "@/apps/credit-line/helpers/authentication.helper";
import { isAndroid } from "@/apps/credit-line/helpers/platformChecker.helper";
import { IPayment } from "@/apps/credit-line/models/CreditLineDetails";
import logAnalyticsEvent from "@/apps/credit-line/services/google-analytics-events.service";
import AnalyticEvents from "@/shared-helpers/google-analytics-events.helper";
import { recorder, RECORDING_APPS } from "@/shared-services/recording.service";
import { DEFAULT_SETTING, Settings } from "@/shared-models/consumer/ISetting";
import DatadogLogger from "@/shared-helpers/datadog-logger.helper";
import { getSettings } from "@/apps/credit-line/helpers/consumerSettings.helper";
import { SettingsCategory } from "@/shared-models/consumer/SettingsCategory";
import { creditLineApiUrls as apiUrl } from "@/apps/credit-line/services/apiClient/apiUrl";
import { useStore } from "vuex";
import { MerchantCountry } from "@/types/merchant";
import AmountPickerV1 from "@/apps/credit-line/components/Payments/AmountPickerV1.vue";
import AmountPickerV2 from "@/apps/credit-line/components/Payments/AmountPickerV2.vue";
import CustomAmountPicker from "@/apps/credit-line/components/Payments/CustomAmountPicker.vue";
import InstructionsContainer from "@/apps/credit-line/components/Payments/InstructionsContainer.vue";
import MxPaymentMethods from "@/apps/credit-line/components/Payments/MxPaymentMethods.vue";
import CoPaymentMethods from "@/apps/credit-line/components/Payments/CoPaymentMethods.vue";
import NoOutstandingBalance from "@/apps/credit-line/components/Payments/NoOutstandingBalance.vue";
import { getRandomNumberBetweenRange } from "@/apps/credit-line/helpers/misc.helper";
import { useCreditLineSummary } from "@/apps/credit-line/hooks/useCreditLineSummary";

declare const Android: AndroidInterface;

const { t } = useI18n();
const pollingTime = ref(3600 * 1000);
const currentStep = ref("");
const country = ref("");
const uiBucket = ref("chooseAmount-b");
const selectedMethod = ref("");
const payableToday = ref();
const kbRef = ref();
const minimumPaymentAmount = ref();
const totalInterestSaved = ref();
const paying = ref(0);
const showDisclaimerMessage = ref(false);
const maximum = ref(0);
const selectedStore = ref("");
const otherNumber = ref("");
const userNumber = ref();
const pickedPhone = ref("thisnumber");
const screenTitle = ref(t("CHOOSEPAYMENTAMOUNT"));
const CreditLineService = new Service();
const paymentInfo = ref({} as IPaymentData);
const lastPayment = ref({} as IPayment);
const fetchingPayment = ref(false);
const noOutstandingBalance = ref<boolean>(false);
const app = getCurrentInstance()?.appContext.app;
const store = useStore();
const { fetchSummary } = useCreditLineSummary();
const whatsappNumber = ref("");
// Alert
const showAlert = ref(false);
const alertMessage = ref("");
const timer = ref();
const showLastSettings = ref(false);
const showBackBtn = computed(() => {
  return !(
    !isAndroid() &&
    (currentStep.value === uiBucket.value ||
      currentStep.value === "instructions")
  );
});

const paymentSettings = ref<Settings>({});

const navbarType = computed(() =>
  noOutstandingBalance.value ? "initial" : "goBack"
);

const showHeader = computed(
  () =>
    !(
      noOutstandingBalance.value &&
      typeof Android.shouldDisplayNewUi() === "boolean"
    )
);

const isStoreCodiOrDealer = computed(() => {
  return (
    selectedStore.value === PaymentMethod.codi ||
    selectedStore.value === PaymentMethod.dealer
  );
});

const storeIsInformativeOnly = computed(() => {
  const optionsWithCode: PaymentMethod[] = [
    PaymentMethod.bancolombia,
    PaymentMethod.efecty,
    PaymentMethod.refacil,
    PaymentMethod.movii,
  ];
  return optionsWithCode.includes(selectedStore.value as PaymentMethod);
});

const displayMxMethods = computed(() => {
  return (
    currentStep.value === "chooseMethod" && country.value === MerchantCountry.MX
  );
});

const displayCoMethods = computed(() => {
  return (
    currentStep.value === "chooseMethod" && country.value === MerchantCountry.CO
  );
});

onBeforeMount(async () => {
  try {
    paymentSettings.value = await getSettings(
      SettingsCategory.CL_PAYMENTS_PAGE
    );
  } catch (error) {
    paymentSettings.value = {};
  }
  getAmounts();
  timer.value = setInterval(() => getAmounts(), pollingTime.value);
});

onMounted(() => {
  recorder(app)?.startRecording(RECORDING_APPS.CREDIT_LINE);
  recorder(app)?.updateRecordingInfo(getRandomNumberBetweenRange(1000000, 1), {
    deviceTag: Android.getDeviceTag(),
  });
});

onBeforeUnmount(() => {
  clearInterval(timer.value);
});

const getAmounts = async () => {
  // Check for new payments experiment.
  // If enabled, exit before fetching summary to avoid network overhead.
  const newPaymentUISetting =
    paymentSettings.value["newPaymentsUi"] ?? DEFAULT_SETTING;
  if (newPaymentUISetting.enabled) {
    handleNewPaymentsUiRedirection(newPaymentUISetting.values?.url as string);
    return;
  }

  try {
    const summary = await fetchSummary();
    if (summary) {
      store.dispatch("creditLine/setCurrency", summary.currency);
      userNumber.value = summary.customerPhone;
      minimumPaymentAmount.value = summary.minimumPaymentAmount;
      payableToday.value = summary.payableToday;
      noOutstandingBalance.value = Number(summary.payableToday) === 0;
      maximum.value = payableToday.value;
      totalInterestSaved.value = summary.totalInterestSaved;
      currentStep.value = uiBucket.value;
      let paymentsLength = summary.payments.length;
      if (paymentsLength > 0) {
        lastPayment.value = summary.payments[paymentsLength - 1] as IPayment;
        if (Object.keys(lastPayment.value).length == 0) {
          showLastSettings.value = false;
        } else if (
          Number(lastPayment.value.paymentAmount) >=
          Number(minimumPaymentAmount.value) + 10
        ) {
          showLastSettings.value = true;
        } else {
          lastPayment.value.paymentAmount = minimumPaymentAmount.value;
          showLastSettings.value = true;
        }
      }
      country.value = summary?.country ?? MerchantCountry.MX;
      if (Android.finishedLoading) {
        Android.finishedLoading();
      }
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    ErrorHandler.logCreditlineErrorResponse(error);
  }
};

const handleNewPaymentsUiRedirection = async (payOptionsUrl: string) => {
  if (isAndroid()) {
    Android.finishedLoading();
  }
  window.location.href = payOptionsUrl + AuthenticationHelper.getDeviceTag();
};

const checkSelectedStore = () => {
  if (selectedStore.value !== PaymentMethod.dealer) {
    switchStep("instructions");
  } else openMap();
};

const closeAlert = (): void => {
  showAlert.value = false;
};

const lastSettings = (): void => {
  let method = lastPayment.value.paymentMethod.toUpperCase();
  if (lastPayment.value.paymentMethod === "stp") {
    method = PaymentMethod.spei;
  }
  if (lastPayment.value.paymentMethod.includes("oxxo")) {
    method = PaymentMethod.oxxo;
  }
  if (lastPayment.value.paymentMethod === "in_store") {
    method = PaymentMethod.dealer;
  }
  paying.value = lastPayment.value.paymentAmount;
  selectedStore.value = method as PaymentMethod;
  checkSelectedStore();
  logAnalyticsEvent(AnalyticEvents.CreditLine.LastPaymentSettings, "Payment");
};

const switchStep = (selected: string) => {
  showAlert.value = false;
  if (selected === "instructions" && !storeIsInformativeOnly.value) {
    fetchingPayment.value = true;
    CreditLineService.requestPayment({
      deviceTag: AuthenticationHelper.getDeviceTag(),
      amount: paying.value,
      payerPhoneNumber:
        pickedPhone.value === "thisnumber"
          ? userNumber.value ?? ""
          : otherNumber.value,
      paymentMethod: selectedStore.value,
    })
      .then((response) => {
        if (response.data?.code) {
          ErrorHandler.logCreditlineErrorResponse(response);
          showAlert.value = true;
          alertMessage.value = getErrorMessage(
            response.data.code || 705,
            t,
            AuthenticationHelper.getDeviceTag(),
            response.data.code === 830
              ? maximum.value
              : minimumPaymentAmount.value
          );
          fetchingPayment.value = false;
        } else {
          paymentInfo.value = response.data as IPaymentData;
          currentStep.value = selected;
          screenTitle.value = t("INSTRUCTIONS");
          fetchingPayment.value = false;
        }
      })
      .catch((err) => {
        if (err.response.data?.errorCode) {
          ErrorHandler.logCreditlineErrorResponse(err.response.data);
          showAlert.value = true;
          alertMessage.value = getErrorMessage(
            err.response.data.errorCode || 705,
            t
          );
          fetchingPayment.value = false;
        }
      });
  } else if (selected === "chooseMethod") {
    if (paying.value > maximum.value) {
      alertMessage.value = t("MAXIMUMWITHDRAWMSG", {
        amount: maximum.value,
      });
      showAlert.value = true;
    } else if (
      paying.value < Number(minimumPaymentAmount.value) ||
      (paying.value > Number(minimumPaymentAmount.value) &&
        paying.value < Number(minimumPaymentAmount.value) + 10)
    ) {
      alertMessage.value = t("MINIMUMWITHDRAWMSG", {
        amount: minimumPaymentAmount.value,
      });
      showAlert.value = true;
    } else {
      currentStep.value = "chooseMethod";
    }
  } else if (storeIsInformativeOnly.value) {
    currentStep.value = selected;
    screenTitle.value = t("INSTRUCTIONS");
    fetchingPayment.value = false;
  }
};

const shouldShowDisclaimerMessage = () => {
  showDisclaimerMessage.value =
    paying.value == minimumPaymentAmount.value ||
    paying.value == payableToday.value;
};

const onChange = (val: string) => {
  const input = Number(val);
  if (isNaN(input)) {
    return kbRef.value.setInput(paying.value.toString());
  }

  if (input > maximum.value) {
    kbRef.value.setInput(Number(payableToday.value).toFixed(2).toString());
    return (paying.value = payableToday.value);
  }

  if (val.split(".")[1] && val.split(".")[1].length > 2) {
    kbRef.value.setInput(paying.value.toString());
    return;
  }

  paying.value = input;
  shouldShowDisclaimerMessage();
};

const selectAmount = async (selected: string) => {
  logAnalyticsEvent(AnalyticEvents.CreditLine[selected], "Payment");
  selectedMethod.value = selected;
  if (selected == "custom") {
    currentStep.value = "keypad";
  } else {
    if (selected === "minimum") {
      paying.value = minimumPaymentAmount.value;
    }
    if (selected === "payoff") {
      paying.value = payableToday.value;
    }
    shouldShowDisclaimerMessage();
    currentStep.value = "chooseMethod";
    screenTitle.value = t("CHOOSEPAYMENTMETHOD");
  }
};

const selectStore = (selected: PaymentMethod) => {
  logAnalyticsEvent(AnalyticEvents.CreditLine[selected], "Payment");
  selectedStore.value = selected;
  if (selected !== PaymentMethod.codi) {
    switchStep("instructions");
  }
};

const setStoreSelected = (selected: PaymentMethod) => {
  selectedStore.value = selected;
};

const openMap = () => {
  const deviceTag = AuthenticationHelper.getDeviceTag();
  window.open(
    apiUrl.CREDIT_LINE_STORE_LOCATOR + "?deviceTag=" + deviceTag,
    "_blank"
  );
};
const goBack = () => {
  if (
    currentStep.value === "instructions" ||
    currentStep.value === uiBucket.value
  ) {
    if (isAndroid()) {
      Android.dismissWebView();
    }
  } else if (
    currentStep.value === "chooseMethod" ||
    currentStep.value === "keypad"
  ) {
    currentStep.value = uiBucket.value;
    screenTitle.value = t("CHOOSEPAYMENTAMOUNT");
  }
};

const confirmPaymentBtn = () => {
  if (!isAndroid()) {
    window.close();
  } else {
    Android.confirmPayment();
  }
};

const openWhatsApp = async () => {
  const helpMsg = t("CL_WP_HELP_MSG", {
    deviceTag: Android.getDeviceTag(),
  });
  try {
    const whatsappNumberSetting =
      paymentSettings.value["whatsAppSupportNumber"] ?? DEFAULT_SETTING;
    whatsappNumber.value =
      (whatsappNumberSetting?.values?.phone as string) || "";
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    DatadogLogger.Log("Failed to fetch WhatsApp number setting", error);
  }
  window.open(
    `https://wa.me/${whatsappNumber.value}?text="${helpMsg}`,
    "_system"
  );
};

const getSelectionStyle = (selected: PaymentMethod) => {
  return (selectedStore.value as PaymentMethod) === selected
    ? "green-option"
    : "regular-option";
};

provide("selectedMethod", selectedMethod);
provide("currentStep", currentStep);
provide("paying", paying);
provide("showDisclaimerMessage", showDisclaimerMessage);
provide("minimumPaymentAmount", minimumPaymentAmount);
provide("navbarType", navbarType);
provide("showHeader", showHeader);
provide("screenTitle", screenTitle);
provide("noOutstandingBalance", noOutstandingBalance);
provide("payableToday", payableToday);
provide("selectAmount", selectAmount);
provide("lastSettings", lastSettings);
provide("showLastSettings", showLastSettings);
provide("lastPayment", lastPayment);
provide("switchStep", switchStep);
provide("onChange", onChange);
provide("currencyFormat", currencyFormat);
provide("kbRef", kbRef);
provide("storeIsInformativeOnly", storeIsInformativeOnly);
provide("selectedStore", selectedStore);
provide("confirmPaymentBtn", confirmPaymentBtn);
provide("openWhatsApp", openWhatsApp);
provide("paymentInfo", paymentInfo);
provide("getSelectionStyle", getSelectionStyle);
provide("selectStore", selectStore);
provide("setStoreSelected", setStoreSelected);
provide("isStoreCodiOrDealer", isStoreCodiOrDealer);
provide("fetchingPayment", fetchingPayment);
provide("checkSelectedStore", checkSelectedStore);
provide("userNumber", userNumber);
provide("pickedPhone", pickedPhone);
provide("otherNumber", otherNumber);
provide("country", country);
provide("whatsappNumber", whatsappNumber);
</script>

<style lang="scss" scoped></style>
